import styled from 'styled-components'

import { Layout } from '@loadsmart/miranda-react'

/**
 * Shared chrome for the signed-out feature previews, Integrations and
 * Scheduler, so the two pages read as one tour.
 */

/* Wide enough for a table, bounded so the prose above it stays readable. */
const CONTENT_MAX_WIDTH = '1120px'

export const StyledContentColumn = styled(Layout.Stack)`
  width: 100%;
  max-width: ${CONTENT_MAX_WIDTH};
  margin: 0 auto;
`

export const StyledSection = styled(Layout.Stack)`
  width: 100%;
`

export const StyledEyebrow = styled.div`
  display: inline-flex;
  /* Sits in a stretching column, so it has to opt out of the full width. */
  align-self: flex-start;
  align-items: center;
  gap: var(--m-spacing-2);
  padding: var(--m-spacing-1) var(--m-spacing-3);
  border-radius: 999px;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  font-weight: var(--m-font-weight-medium);
  color: rgb(var(--m-color-primary-100));
  background-color: rgb(var(--m-color-background-highlight));
`

export const StyledHeadline = styled.h1`
  margin: 0;
  max-width: 26ch;
  font-family: var(--m-font-family-title);
  font-size: var(--m-font-size-8);
  font-weight: var(--m-font-weight-bold);
  line-height: 112%;
  letter-spacing: -0.02em;
  color: rgb(var(--m-color-primary-100));

  @media screen and (max-width: 720px) {
    font-size: var(--m-font-size-7);
  }
`

export const StyledHeadlineSoft = styled.span`
  color: rgb(var(--m-color-neutral-60));
`

export const StyledLede = styled.p`
  margin: 0;
  max-width: 62ch;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-4);
  line-height: 160%;
  color: rgb(var(--m-color-text-tertiary));
`

export const StyledActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--m-spacing-3);
`

export const StyledDisclaimer = styled.p`
  display: flex;
  align-items: center;
  gap: var(--m-spacing-2);
  margin: 0;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-text-placeholder));
`

/* --- the product, with sample data in it ---------------------------------- */

export const StyledPanel = styled.div`
  overflow: hidden;
  background-color: rgb(var(--m-color-background-primary));
  border: var(--m-border-thin) solid rgb(var(--m-color-border));
  border-radius: var(--m-border-radius-m);
  box-shadow: var(--m-elevation-1);
`

export const StyledPanelHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--m-spacing-3);
  padding: var(--m-spacing-4) var(--m-spacing-5);
  border-bottom: var(--m-border-thin) solid rgb(var(--m-color-border));
`

export const StyledPanelTitle = styled.h2`
  margin: 0;
  /* Takes the slack so the meta and controls sit at the right edge. */
  flex: 1 1 auto;
  font-family: var(--m-font-family-title);
  font-size: var(--m-font-size-5);
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-primary-100));
`

export const StyledPanelBadge = styled.span`
  flex: 0 0 auto;
  padding: 2px var(--m-spacing-2);
  border-radius: 999px;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-1);
  font-weight: var(--m-font-weight-bold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgb(var(--m-color-primary-100));
  background-color: rgb(var(--m-color-background-highlight));
`

export const StyledPanelMeta = styled.span`
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  color: rgb(var(--m-color-text-tertiary));
`

export const StyledPanelBody = styled.div`
  padding: var(--m-spacing-5);
`

/* Scroll rather than squeezing the columns until the datetimes wrap. */
export const StyledTableScroller = styled.div`
  overflow-x: auto;
`

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: var(--m-font-family-default);
  white-space: nowrap;
`

export const StyledHeaderCell = styled.th<{ $align?: 'left' | 'center' }>`
  padding: var(--m-spacing-3) var(--m-spacing-5);
  text-align: ${({ $align }) => $align ?? 'left'};
  font-size: var(--m-font-size-2);
  font-weight: var(--m-font-weight-bold);
  color: rgb(var(--m-color-text-secondary));
  background-color: rgb(var(--m-color-background-secondary));
  border-bottom: var(--m-border-thin) solid rgb(var(--m-color-border));
`

export const StyledRow = styled.tr<{ $highlight?: boolean }>`
  background-color: ${({ $highlight }) =>
    $highlight ? 'rgba(var(--m-color-danger-20), 0.55)' : 'transparent'};

  & + & td {
    border-top: var(--m-border-thin) solid rgb(var(--m-color-border));
  }
`

export const StyledCell = styled.td<{ $align?: 'left' | 'center' }>`
  padding: var(--m-spacing-3) var(--m-spacing-5);
  text-align: ${({ $align }) => $align ?? 'left'};
  font-size: var(--m-font-size-3);
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-text-primary));
`

export const StyledCellMuted = styled.span`
  color: rgb(var(--m-color-text-tertiary));
`

export const StyledStatusPill = styled.span<{ $tone: 'success' | 'danger' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: var(--m-spacing-1);
  padding: 2px var(--m-spacing-2);
  border-radius: 999px;
  font-size: var(--m-font-size-2);
  font-weight: var(--m-font-weight-medium);
  color: ${({ $tone }) =>
    $tone === 'danger'
      ? 'rgb(var(--m-color-danger-100))'
      : $tone === 'success'
        ? 'rgb(var(--m-color-success-100))'
        : 'rgb(var(--m-color-text-tertiary))'};
  background-color: ${({ $tone }) =>
    $tone === 'danger'
      ? 'rgb(var(--m-color-danger-20))'
      : $tone === 'success'
        ? 'rgb(var(--m-color-success-20))'
        : 'rgb(var(--m-color-background-secondary))'};
`

// Annotates one row of the preview, so the table is explained and not just shown.
export const StyledCallout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--m-spacing-3);
  padding: var(--m-spacing-4) var(--m-spacing-5);
  border-top: var(--m-border-thin) solid rgb(var(--m-color-border));
  background-color: rgb(var(--m-color-background-secondary));
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  line-height: var(--m-line-height-6);
  color: rgb(var(--m-color-text-secondary));
`

export const StyledCalloutMarker = styled.span`
  flex: 0 0 auto;
  margin-top: 2px;
  color: rgb(var(--m-color-danger-100));
`

/* --- segmented control ---------------------------------------------------- */

export const StyledSegmented = styled.div`
  display: inline-flex;
  flex: 0 0 auto;
  padding: 2px;
  gap: 2px;
  border-radius: var(--m-border-radius-s);
  background-color: rgb(var(--m-color-background-secondary));
`

export const StyledSegment = styled.button<{ $active: boolean }>`
  padding: var(--m-spacing-1) var(--m-spacing-3);
  cursor: pointer;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  font-weight: var(--m-font-weight-medium);
  border: none;
  border-radius: var(--m-border-radius-s);
  color: ${({ $active }) =>
    $active ? 'rgb(var(--m-color-primary-100))' : 'rgb(var(--m-color-text-tertiary))'};
  background-color: ${({ $active }) =>
    $active ? 'rgb(var(--m-color-background-primary))' : 'transparent'};
  box-shadow: ${({ $active }) => ($active ? 'var(--m-elevation-1)' : 'none')};

  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: 1px;
  }
`

/* --- why it matters ------------------------------------------------------- */

export const StyledHighlightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--m-spacing-4);
`

export const StyledHighlight = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-2);
  padding: var(--m-spacing-5);
  background-color: rgb(var(--m-color-background-primary));
  border: var(--m-border-thin) solid rgb(var(--m-color-border));
  border-radius: var(--m-border-radius-m);
`

/* --- closing -------------------------------------------------------------- */

export const StyledClosingBand = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-5);
  padding: var(--m-spacing-8);
  background-color: rgb(var(--m-color-primary-100));
  border-radius: var(--m-border-radius-l);

  @media screen and (min-width: 900px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

export const StyledClosingCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-2);
  max-width: 52ch;
`

export const StyledClosingTitle = styled.h2`
  margin: 0;
  font-family: var(--m-font-family-title);
  font-size: var(--m-font-size-6);
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-text-inverted));
`

export const StyledClosingBody = styled.p`
  margin: 0;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  line-height: var(--m-line-height-6);
  color: rgba(var(--m-color-neutral-0), 0.68);
`

// Miranda's buttons lose their edges on this dark band, so this one is ours.
export const StyledClosingAction = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: var(--m-spacing-2);
  padding: var(--m-spacing-3) var(--m-spacing-5);
  cursor: pointer;
  white-space: nowrap;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  font-weight: var(--m-font-weight-medium);
  color: rgb(var(--m-color-primary-100));
  background-color: rgb(var(--m-color-brand));
  border: var(--m-border-thin) solid rgb(var(--m-color-brand));
  border-radius: 999px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgb(var(--m-color-brand-accent-2));
    border-color: rgb(var(--m-color-brand-accent-2));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: 2px;
  }
`
